import { useMemo, useState } from "react";

import useNow from "@/utils/hooks/useNow";
import Valkyrie, { viArrowLeft, viArrowRight, viCalendar, viCalendarClock } from "@sippy-platform/valkyrie";

import { Button, Grid, List, ListItem, ListItemButton, ListItemText, Popover, Stack, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import {
  addDays,
  addHours,
  endOfDay,
  endOfISOWeek,
  endOfMonth,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMinute,
  isSameMonth,
  parseISO,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
  subDays,
  subHours,
  subMonths,
} from "date-fns";
import PropTypes from "prop-types";

function DateFilter({ startDate, endDate, setFilter }) {
  const dateFormat = "yyyy-MM-dd'T'HH:mm:ss.SSS";

  const [dateFilterAnchorEl, setDateFilterAnchorEl] = useState(null);

  const [startDateValue, setStartDateValue] = useState(startDate);
  const [endDateValue, setEndDateValue] = useState(endDate);

  const open = Boolean(dateFilterAnchorEl);

  const now = useNow();
  const ranges = useMemo(
    () => [
      [
        {
          name: "Today",
          start: format(startOfDay(now), dateFormat),
          end: format(endOfDay(now), dateFormat),
        },
        {
          name: "Yesterday",
          start: format(startOfDay(subDays(now, 1)), dateFormat),
          end: format(endOfDay(subDays(now, 1)), dateFormat),
        },
        {
          name: "Day before yesterday",
          start: format(startOfDay(subDays(now, 2)), dateFormat),
          end: format(endOfDay(subDays(now, 2)), dateFormat),
        },
        {
          name: "This day last week",
          start: format(startOfDay(subDays(now, 7)), dateFormat),
          end: format(endOfDay(subDays(now, 7)), dateFormat),
        },
        {
          name: "This week",
          start: format(startOfISOWeek(now), dateFormat),
          end: format(endOfISOWeek(now), dateFormat),
        },
        {
          name: "Last week",
          start: format(startOfISOWeek(subDays(now, 7)), dateFormat),
          end: format(endOfISOWeek(subDays(now, 7)), dateFormat),
        },
      ],
      Array.from({ length: 6 }, (_, index) => ({
        name: format(startOfMonth(subMonths(now, index)), "MMMM yyyy"),
        start: format(startOfMonth(subMonths(now, index)), dateFormat),
        end: format(endOfMonth(subMonths(now, index)), dateFormat),
      })),
      [1, 3, 6, 12, 24, 48].map((hours) => ({
        name: `Last ${hours} hour${hours === 1 ? "" : "s"}`,
        start: format(subHours(now, hours), dateFormat),
        end: format(now, dateFormat),
      })),
    ],
    [now],
  );

  const activeRange = useMemo(() => {
    const presetRange = ranges.flat().find((range) => range.start === startDate && range.end === endDate);
    if (presetRange) {
      return presetRange;
    }

    if (
      isSameMinute(startDate, startOfMonth(startDate)) &&
      isSameMinute(endDate, endOfMonth(endDate)) &&
      isSameMonth(startDate, endDate)
    ) {
      return { name: format(startDate, "MMMM yyyy") };
    } else if (
      isSameMinute(startDate, startOfDay(startDate)) &&
      isSameMinute(endDate, endOfDay(endDate)) &&
      isSameDay(startDate, endDate)
    ) {
      return { name: format(startDate, "PPP") };
    } else if (isSameDay(startDate, endDate)) {
      return { name: `${format(startDate, "PPp")} - ${format(endDate, "p")}` };
    } else {
      return { name: `${format(startDate, "PPp")} - ${format(endDate, "PPp")}` };
    }
  }, [endDate, startDate, ranges]);

  // Handle filters
  function setActiveRange(range) {
    setStartDateValue(range.start);
    setEndDateValue(range.end);
    setFilter("startDate", range.start);
    setFilter("endDate", range.end);
    setDateFilterAnchorEl(null);
  }

  const handleStartDateBlur = () => {
    const date = parseISO(startDateValue);

    if (isAfter(date, parseISO(endDateValue))) {
      setEndDateValue(format(addHours(date, 1), dateFormat));
    } else if (isBefore(date, subDays(parseISO(endDateValue), 31))) {
      setEndDateValue(format(addDays(date, 31), dateFormat));
    }
  };

  const handleEndDateBlur = () => {
    const date = parseISO(endDateValue);

    if (isBefore(date, parseISO(startDateValue))) {
      setStartDateValue(format(subHours(date, 1), dateFormat));
    } else if (isAfter(date, addDays(parseISO(startDateValue), 31))) {
      setStartDateValue(format(subDays(date, 31), dateFormat));
    }
  };

  return (
    <>
      <Button
        color="inherit"
        id="select-range"
        onClick={(event) => setDateFilterAnchorEl(event.currentTarget)}
        startIcon={<Valkyrie icon={viCalendar} />}
      >
        {activeRange.name}
      </Button>
      <Popover
        open={open}
        anchorEl={dateFilterAnchorEl}
        onClose={() => {
          setDateFilterAnchorEl(null);
          setFilter("startDate", startDateValue);
          setFilter("endDate", endDateValue);
        }}
        slotProps={{
          paper: {
            elevation: 1,
            sx: {
              border: "1px solid",
              borderColor: "divider",
              p: 1.5,
              width: { xs: 300, sm: 600 },
            },
          },
        }}
      >
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Typography variant="h6" component="h4" sx={{ m: 0 }}>
            Quick ranges
          </Typography>
          <Grid container spacing={0.25}>
            {ranges.map((rangeSet, key) => (
              <Grid key={key} size={{ xs: 12, sm: 4 }}>
                <List>
                  {rangeSet.map((rangeItem, key) => (
                    <ListItem disablePadding key={key} sx={{ minHeight: 32 }}>
                      <ListItemButton
                        selected={activeRange.name === rangeItem.name}
                        onClick={() => setActiveRange(rangeItem)}
                        sx={{ minHeight: 32 }}
                      >
                        <ListItemText primary={rangeItem.name} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Grid>
            ))}
          </Grid>
          <Typography variant="h6" component="h4" sx={{ m: 0 }}>
            Custom (maximum 31 days)
          </Typography>
          <Stack
            direction="row"
            sx={{
              gap: 1,
            }}
          >
            <DateTimePicker
              showDaysOutsideCurrentMonth
              label="From"
              id="startDate"
              value={parseISO(startDateValue)}
              onChange={(date) => setStartDateValue(format(date, dateFormat))}
              onClose={handleStartDateBlur}
              format="dd-MM-yyyy HH:mm:ss"
              slots={{
                leftArrowIcon: () => <Valkyrie icon={viArrowLeft} />,
                openPickerIcon: () => <Valkyrie icon={viCalendarClock} />,
                rightArrowIcon: () => <Valkyrie icon={viArrowRight} />,
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  onBlur: handleStartDateBlur,
                },
              }}
            />
            <DateTimePicker
              showDaysOutsideCurrentMonth
              label="Until"
              id="endDate"
              value={parseISO(endDateValue)}
              onChange={(date) => setEndDateValue(format(date, dateFormat))}
              onClose={handleEndDateBlur}
              format="dd-MM-yyyy HH:mm:ss"
              slots={{
                leftArrowIcon: () => <Valkyrie icon={viArrowLeft} />,
                openPickerIcon: () => <Valkyrie icon={viCalendarClock} />,
                rightArrowIcon: () => <Valkyrie icon={viArrowRight} />,
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  onBlur: handleEndDateBlur,
                },
              }}
            />
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}

DateFilter.propTypes = {
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  setFilter: PropTypes.func,
};

export default DateFilter;
